import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface BootApp {
  appId: string;
  displayName: string;
  webUrl: string;
  iconUrl?: string;
  status: string;
  order: number;
  mobileScheme?: string;
  /** Extended: structured icon data from Central v1.1 (shell adapter pass-through) */
  assets?: {
    icon?: {
      iconKey?: string;
      iconUrl?: string;
      iconVersion?: number;
      monochrome?: boolean;
    };
  };
  /** Extended: UI hints from Central (shell adapter pass-through for theme/subtitle) */
  ui?: {
    subtitle?: string;
    theme?: Record<string, string>;
  };
}

export interface BootWorkspace {
  id: string;
  name: string;
  role: string;
  status?: string;
  /** URL-safe slug from Central (shell adapter pass-through) */
  slug?: string;
  /** Workspace type from Central (shell adapter pass-through) */
  type?: 'personal' | 'business';
  /** Whether this is the user's primary workspace (shell adapter pass-through) */
  isPrimary?: boolean;
  /** Capabilities from Central (when meta.features.capabilities is true) */
  capabilities?: Record<string, Record<string, Record<string, boolean>>>;
  /** Workspace-level permissions from Central (when meta.features.workspacePermissions is true) */
  permissions?: Record<string, boolean>;
}

export interface BootUser {
  id: string;
  email: string;
  name?: string;
  /** Full display name from Central (may differ from name) */
  fullName?: string | null;
  /** Avatar URL from Central */
  avatarUrl?: string | null;
  /** LevelUp-internal users only — gates testing-mode UI */
  platformInternal?: boolean;
  /** Super-admin on the platform (broader than platformInternal) */
  platformSuperAdmin?: boolean;
}

/**
 * Central switch-context metadata.
 * Controls feature flags and cache behavior for the boot context.
 */
export interface BootMeta {
  cacheTtlSeconds?: number;
  features?: {
    capabilities?: boolean;
    workspacePermissions?: boolean;
    uiShell?: boolean;
    uiShellAssets?: boolean;
    /** Enables the testing-mode flask in the shell (platform-internal users only) */
    testingMode?: boolean;
  };
  /** Current testing-mode state from Central (when features.testingMode is enabled) */
  testingMode?: {
    active?: boolean;
    override?: {
      workspaceId: string;
      workspaceName?: string;
      appId: string;
      appName?: string;
      role: string;
    };
  };
  /** Shell layout/iconography hints from Central (pass-through for ui-shell) */
  uiShell?: {
    layout?: {
      gutter?: number;
      navWidth?: { collapsed?: number; expanded?: number };
    };
    iconography?: { iconSet?: string };
  };
  uiHints?: { createWorkspaceUrl?: string };
}

export interface BootContext {
  user: BootUser;
  currentWorkspace: BootWorkspace;
  otherWorkspaces: BootWorkspace[];
  apps: BootApp[];
  entitlementsEffective: string[];
  /** Metadata from Central switch-context response */
  meta?: BootMeta | null;
}

type BootStatus = 'idle' | 'loading' | 'ready' | 'workspace_closed' | 'central_unavailable';

interface BootState {
  status: BootStatus;
  context: BootContext | null;
  error: string | null;
  closedWorkspaceName: string | null;
  closedOtherWorkspaces: BootWorkspace[] | null;
}

const initialState: BootState = {
  status: 'idle',
  context: null,
  error: null,
  closedWorkspaceName: null,
  closedOtherWorkspaces: null,
};

export const bootSlice = createSlice({
  name: 'boot',
  initialState,
  reducers: {
    bootStarted: (state) => {
      state.status = 'loading';
      state.error = null;
    },
    bootSuccess: (state, action: PayloadAction<BootContext>) => {
      state.status = 'ready';
      state.context = action.payload;
      state.error = null;
      state.closedWorkspaceName = null;
      state.closedOtherWorkspaces = null;
    },
    bootWorkspaceClosed: (
      state,
      action: PayloadAction<{ workspaceName: string; otherWorkspaces: BootWorkspace[] }>
    ) => {
      state.status = 'workspace_closed';
      state.closedWorkspaceName = action.payload.workspaceName;
      state.closedOtherWorkspaces = action.payload.otherWorkspaces;
      state.error = null;
    },
    bootCentralUnavailable: (state, action: PayloadAction<string>) => {
      state.status = 'central_unavailable';
      state.error = action.payload;
    },
    /**
     * Client-side workspace switch.
     * Swaps currentWorkspace with the target workspace from otherWorkspaces.
     * No OIDC re-auth needed — X-Workspace-Id header tells the backend which workspace to use.
     */
    switchWorkspace: (state, action: PayloadAction<string>) => {
      if (!state.context) return;
      const targetId = action.payload;
      const targetIndex = state.context.otherWorkspaces.findIndex(
        (ws) => ws.id === targetId
      );
      if (targetIndex === -1) return;

      const target = state.context.otherWorkspaces[targetIndex]!;
      const previous = state.context.currentWorkspace;

      // Swap: target becomes current, previous goes into others
      state.context.currentWorkspace = target;
      state.context.otherWorkspaces = [
        previous,
        ...state.context.otherWorkspaces.filter((ws) => ws.id !== targetId),
      ];
    },
    bootReset: () => initialState,
  },
});

export const {
  bootStarted,
  bootSuccess,
  bootWorkspaceClosed,
  bootCentralUnavailable,
  switchWorkspace,
  bootReset,
} = bootSlice.actions;
